import React, { useState, useEffect, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import Column, { AutoColumn, ColumnStart } from 'components/Column'
import { TYPE } from 'theme'
import { ResponsiveRow, RowBetween, RowFixed, TableRowBetween } from 'components/Row'
import LineChart from 'components/LineChart/alt'
import useTheme from 'hooks/useTheme'
import { useProtocolChartData, useProtocolData, useProtocolTransactions } from 'state/protocol/hooks'
import { ChartCard } from 'components/Card'
import { formatAmount, formatDollarAmount } from 'utils/numbers'
import Percent from 'components/Percent'
import { HideMedium, HideSmall, StyledInternalLink } from '../../theme/components'
import TokenTable from 'components/tokens/TokenTable'
import PoolTable from 'components/pools/PoolTable'
import { PageWrapper, ThemedBackgroundGlobal } from 'pages/styled'
import { unixToDate } from 'utils/date'
import BarChart from 'components/BarChart/alt'
import { useAllPoolData } from 'state/pools/hooks'
import { notEmpty } from 'utils'
import TransactionsTable from '../../components/TransactionsTable'
import { useAllTokenData } from 'state/tokens/hooks'
import { MonoSpace } from 'components/shared'
import { useActiveNetworkVersion } from 'state/application/hooks'
import { useTransformedVolumeData, useTransformedTVLData } from 'hooks/chart'
import { SmallOptionButton } from 'components/Button'
import { VolumeWindow } from 'types'
import CalendarIcon from '../../assets/svg/calendar.svg'
import EqualizerIcon from '../../assets/svg/equalizer.svg'
import InsightsIcon from '../../assets/svg/insights.svg'
import LockIcon from '../../assets/svg/lock.svg'
import ArrowUpIcon from '../../assets/svg/arrow-up.svg'
import ArrowDownIcon from '../../assets/svg/arrow-down.svg'
import { Link } from 'react-router-dom'
import ArrowRightIcon from '../../assets/svg/arrow-right.svg'
import EmptyHourGlassIcon from '../../assets/svg/empty-hourglass.svg'
import LockResetIcon from '../../assets/svg/lock-reset.svg'
import AccountIcon from '../../assets/svg/account-circle.svg'
import ScheduleIcon from '../../assets/svg/schedule-clock.svg'
import ChronosHourglass from '../../assets/svg/hourglass.svg'
import TopPositionIcon from '../../assets/svg/cup.svg'
import GaugeTable from 'components/gauges/GaugeTable'
import { EPOCH_0_START } from 'constants/Constants'
import moment from 'moment'
import { useTotalVeChr } from 'state/voter/hooks'
import { useWeb3React } from '@web3-react/core'
import Carousel from 'components/Carousel'
interface DailyInfoElementProps {
  title: string
  icon: any
  value: string
  percentageChange?: number
  timeInfo?: string
  isMobile?: boolean
}

const ChartWrapper = styled(ChartCard)`
  width: 49%;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 90vw;
  `};
`

const InfoWrapper = styled.div`
  display: flex;
  position: relative;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.bg1};
  padding: 12px 32px;
  height: 100%;
  justify-content: center;
  align-items: center;
  width: 60%;

  ${({ theme }) => theme.mediaWidth.upToSmall`
  padding: 0 12px;
  width: fit-content;
  gap: 8px;
  `};
`

const LeftAlignContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  justify-content: center;
`

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
`

const InfoSeparator = styled.div`
  height: 85%;
  width: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: absolute;
`
const TimeVolumeContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`
const WindowSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.bg2};
  border-radius: 20px;
`

const SwitchOption = styled.span<{ active?: boolean }>`
  font-size: 14px;
  background: ${({ active }) => (active ? 'linear-gradient(67.55deg, #3f4ab3 4.5%, #7a64d0 95.77%)' : 'none')};
  -webkit-background-clip: text;
  -webkit-text-fill-color: ${({ active }) => (active ? 'transparent' : '#FFFFFF')};
  background-clip: text;
  color: ${({ active }) => (active ? 'transparent' : '#FFFFFF')};
`

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 20px;
  flex: 1;
  background-color: ${({ theme }) => theme.bg2};
  border: 1px solid ${({ theme }) => theme.border1};
  width: 100%;

  ${({ theme }) => theme.mediaWidth.upToSmall`
  min-width: 350px;
  `};
`

const IconContainer = styled.div`
  height: 125px;
  min-width: 125px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #322d43;
  margin-right: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  ${({ theme }) => theme.mediaWidth.upToSmall`
  height: 90px;
  min-width: 90px;
  `};
`

const InfoMainContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const InfoTitle = styled.span`
  font-size: 12px;
  margin-bottom: 5px;
`

const InfoValueContainer = styled.div`
  display: flex;
  align-items: center;
`

const InfoValue = styled.span`
  font-size: 16px;
  margin-right: 10px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
  font-size: 16px;
  `};
`

const InfoValueChange = styled.div<{ isNegative?: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid ${({ isNegative }) => (isNegative ? '#FF2A5F' : '#27F291')}
  border-radius: 20px;
  padding: 5px 10px;
`

const ChangeValue = styled.span<{ isNegative?: boolean }>`
  color: ${({ isNegative }) => (isNegative ? '#FF2A5F' : '#27F291')};
  font-size: 12px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
  font-size: 10px;
  `};
`

const InfoRowContainer = styled.div`
  width: 100%;
  display: grid;
  gap: 30px;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  grid-template-rows: 1fr;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    flex-direction: column;
  `};
`

const ExploreLink = styled(Link)<{ fontSize?: string }>`
  cursor: pointer;
  position: relative; /* Add position relative to the parent element */
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  background-color: #3b3855;
  padding: 0.625rem 1.25rem;
  gap: 0.625rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(67.55deg, #2f3bae 4.5%, #9a81ff 95.77%);
    opacity: 0;
    border-radius: inherit;
    transition: opacity 0.3s ease; /* Add transition property for opacity */
  }

  :hover::before {
    opacity: 1; /* Change the opacity on hover to reveal the background gradient */
  }

  :focus {
    outline: none;
    text-decoration: none;
  }

  :active {
    text-decoration: none;
  }
`

const ExploreIconContainer = styled.div`
  z-index: 2;
  display: flex;
  align-items: center;
`

const LinkContent = styled.span`
  z-index: 2;
  color: ${({ theme }) => theme.text1};
  font-weight: 500;
  font-size: 14px;
`

const PageSubHeader = styled.span`
  font-size: 30px;

  @media (max-width: 1080px) {
    font-size: 24px;
  }

  ${({ theme }) => theme.mediaWidth.upToSmall`
  font-size: 18px;
  `};
`
const ChartTitle = styled.span`
  font-size: 25px;
  color: ${({ theme }) => theme.textWithOpacity};
  ${({ theme }) => theme.mediaWidth.upToSmall`
font-size: 12px;
`};
`

const ChartValue = styled.span`
  font-size: 25px;
  ${({ theme }) => theme.mediaWidth.upToSmall`
font-size: 18px;
`};
`

const ChartSubTitle = styled.span`
  font-size: 12px;
  ${({ theme }) => theme.mediaWidth.upToSmall`
font-size: 10px;
`};
`

const TimeInfoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #6c74c3;
  border-radius: 20px;
  padding: 5px 10px;
  gap: 4px;
`

const TimeInfoContent = styled.span`
  color: #6c74c3;
  font-size: 12px;
`

const elementWidth = 350

const scrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-${elementWidth * 2}px);
  }
`

const AnimatedElements = styled.div`
  width: 90vw;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ScrollContainer = styled.div`
  display: flex;
  gap: 15px;
  width: 100%;
  animation: ${scrollAnimation} 10s linear infinite alternate;
`

const ReverseScrollContainer = styled(ScrollContainer)`
  animation: ${scrollAnimation} 10s linear infinite alternate-reverse;
`

export const DailyInfoElement: React.FC<DailyInfoElementProps> = ({
  title,
  icon,
  value,
  percentageChange,
  timeInfo,
  isMobile = false,
}: any) => {
  const truncated = parseFloat(percentageChange?.toFixed(2))

  return (
    <ContentContainer>
      <IconContainer>
        <img width={isMobile ? '40px' : '60px'} height={isMobile ? '40px' : '60px'} alt="icon" src={icon} />
      </IconContainer>
      <InfoMainContainer>
        <InfoTitle>{title}</InfoTitle>
        <InfoValueContainer>
          <InfoValue>{value}</InfoValue>
          {percentageChange && (
            <InfoValueChange isNegative={truncated < 0}>
              {truncated > 0 ? (
                <img width="17px" height="17px" alt="arrow" src={ArrowUpIcon} />
              ) : (
                <img width="17px" height="17px" alt="arrow" src={ArrowDownIcon} />
              )}

              <ChangeValue isNegative={truncated < 0}>{Math.abs(percentageChange).toFixed(2)}%</ChangeValue>
            </InfoValueChange>
          )}
          {timeInfo && (
            <TimeInfoContainer>
              <img src={ScheduleIcon} alt="schedule" />
              <TimeInfoContent>{timeInfo}</TimeInfoContent>
            </TimeInfoContainer>
          )}
        </InfoValueContainer>
      </InfoMainContainer>
    </ContentContainer>
  )
}

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const theme = useTheme()

  const [activeNetwork] = useActiveNetworkVersion()

  const [protocolData] = useProtocolData()
  const [transactions] = useProtocolTransactions()

  const [volumeHover, setVolumeHover] = useState<number | undefined>()
  const [liquidityHover, setLiquidityHover] = useState<number | undefined>()
  const [leftLabel, setLeftLabel] = useState<string | undefined>()
  const [rightLabel, setRightLabel] = useState<string | undefined>()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 720)

  // Hot fix to remove errors in TVL data while subgraph syncs.
  const [chartData] = useProtocolChartData()

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 720)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    setLiquidityHover(undefined)
    setVolumeHover(undefined)
  }, [activeNetwork])

  // get all the pool datas that exist
  const allPoolData = useAllPoolData()
  const gaugeDatas = useMemo(() => {
    return Object.values(allPoolData).filter(notEmpty)
  }, [allPoolData])
  // const poolDatas = useMemo(() => {
  //   return Object.values(allPoolData)
  //     .map((p) => p.data)
  //     .filter(notEmpty)
  // }, [allPoolData])

  // if hover value undefined, reset to current day value
  useEffect(() => {
    if (volumeHover === undefined && protocolData) {
      setVolumeHover(protocolData.volumeUSD)
    }
  }, [protocolData, volumeHover])
  useEffect(() => {
    if (liquidityHover === undefined && protocolData) {
      setLiquidityHover(protocolData.tvlUSD)
    }
  }, [liquidityHover, protocolData])

  const formattedTvlData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: unixToDate(day.date),
          value: day.tvlUSD,
        }
      })
    } else {
      return []
    }
  }, [chartData])

  const formattedVolumeData = useMemo(() => {
    if (chartData) {
      return chartData.map((day) => {
        return {
          time: unixToDate(day.date),
          value: day.volumeUSD,
        }
      })
    } else {
      return []
    }
  }, [chartData])

  const weeklyVolumeData = useTransformedVolumeData(chartData, 'week')
  const monthlyVolumeData = useTransformedVolumeData(chartData, 'month')

  const weeklyTVLData = useTransformedTVLData(chartData, 'week')
  const monthlyTVLData = useTransformedTVLData(chartData, 'month')

  const allTokens = useAllTokenData()

  const formattedTokens = useMemo(() => {
    return Object.values(allTokens)
      .map((t) => t.data)
      .filter(notEmpty)
  }, [allTokens])

  const [volumeWindow, setVolumeWindow] = useState(VolumeWindow.daily)
  const [tvlWindow, setTvlWindow] = useState(VolumeWindow.daily)

  const tvlValue = useMemo(() => {
    if (liquidityHover) {
      return formatDollarAmount(liquidityHover, 2, true)
    }
    return formatDollarAmount(protocolData?.tvlUSD, 2, true)
  }, [liquidityHover, protocolData?.tvlUSD])

  const getCurrentEpoch = () => {
    // Calculate current epoch week difference from epoch 0
    const currentEpoch = Math.floor((new Date().getTime() / 1000 - EPOCH_0_START) / (7 * 24 * 60 * 60))

    return currentEpoch
  }

  const getEpochStart = () => {
    const epochStart = Math.floor(new Date().getTime() / 1000 / (7 * 24 * 60 * 60)) * (7 * 24 * 60 * 60)
    return moment.unix(epochStart).format('DD-MM-YYYY')
  }

  const getEpochEnd = () => {
    const epochEnd = Math.ceil(new Date().getTime() / 1000 / (7 * 24 * 60 * 60)) * (7 * 24 * 60 * 60)
    return moment.unix(epochEnd).format('DD-MM-YYYY')
  }

  const [veChrAmount] = useTotalVeChr()

  return (
    <PageWrapper>
      <ThemedBackgroundGlobal backgroundColor={activeNetwork.bgColor} />
      <AutoColumn gap="16px" padding="18px">
        <PageSubHeader>Analytics Overview</PageSubHeader>

        {isMobile ? (
          <Carousel>
            <ChartWrapper>
              <LineChart
                data={
                  tvlWindow === VolumeWindow.monthly
                    ? monthlyTVLData
                    : tvlWindow === VolumeWindow.weekly
                    ? weeklyTVLData
                    : formattedTvlData
                }
                height={220}
                minHeight={332}
                value={liquidityHover}
                label={leftLabel}
                setValue={setLiquidityHover}
                setLabel={setLeftLabel}
                activeWindow={tvlWindow}
                topRight={
                  <WindowSwitchContainer>
                    <SmallOptionButton onClick={() => setTvlWindow(VolumeWindow.daily)}>
                      <SwitchOption active={tvlWindow === VolumeWindow.daily}>D</SwitchOption>
                    </SmallOptionButton>
                    {/* <SmallOptionButton style={{ marginLeft: '8px' }} onClick={() => setTvlWindow(VolumeWindow.weekly)}>
                      <SwitchOption active={tvlWindow === VolumeWindow.weekly}>W</SwitchOption>
                    </SmallOptionButton>
                    <SmallOptionButton style={{ marginLeft: '8px' }} onClick={() => setTvlWindow(VolumeWindow.monthly)}>
                      <SwitchOption active={tvlWindow === VolumeWindow.monthly}>M</SwitchOption>
                </SmallOptionButton> */}
                  </WindowSwitchContainer>
                }
                topLeft={
                  <InfoWrapper>
                    <Column>
                      <ChartTitle>TVL</ChartTitle>
                      <ChartValue>{tvlValue}</ChartValue>
                    </Column>
                    <ColumnStart>
                      <TimeVolumeContainer>
                        <img width={'20px'} height={'20px'} src={CalendarIcon} alt="Calendar" />
                        <ChartSubTitle>{leftLabel ? <MonoSpace>{leftLabel}</MonoSpace> : 'All time'}</ChartSubTitle>
                      </TimeVolumeContainer>
                    </ColumnStart>
                  </InfoWrapper>
                }
              />
            </ChartWrapper>
            <ChartWrapper>
              <BarChart
                height={220}
                minHeight={332}
                data={
                  volumeWindow === VolumeWindow.monthly
                    ? monthlyVolumeData
                    : volumeWindow === VolumeWindow.weekly
                    ? weeklyVolumeData
                    : formattedVolumeData
                }
                setValue={setVolumeHover}
                setLabel={setRightLabel}
                value={volumeHover}
                label={rightLabel}
                activeWindow={volumeWindow}
                topRight={
                  <WindowSwitchContainer>
                    <SmallOptionButton onClick={() => setVolumeWindow(VolumeWindow.daily)}>
                      <SwitchOption active={volumeWindow === VolumeWindow.daily}>D</SwitchOption>
                    </SmallOptionButton>
                    {/*<SmallOptionButton
                      style={{ marginLeft: '8px' }}
                      onClick={() => setVolumeWindow(VolumeWindow.weekly)}
                    >
                      <SwitchOption active={volumeWindow === VolumeWindow.weekly}>W</SwitchOption>
                    </SmallOptionButton>
                    <SmallOptionButton
                      style={{ marginLeft: '8px' }}
                      onClick={() => setVolumeWindow(VolumeWindow.monthly)}
                    >
                      <SwitchOption active={volumeWindow === VolumeWindow.monthly}>M</SwitchOption>
                </SmallOptionButton>*/}
                  </WindowSwitchContainer>
                }
                topLeft={
                  <InfoWrapper>
                    <Column>
                      <ChartTitle>Volume</ChartTitle>
                      <ChartValue>{tvlValue}</ChartValue>
                    </Column>
                    <ColumnStart>
                      <TimeVolumeContainer>
                        <img width={'20px'} height={'20px'} src={CalendarIcon} alt="Calendar" />
                        <ChartSubTitle>{leftLabel ? <MonoSpace>{leftLabel}</MonoSpace> : 'All time'}</ChartSubTitle>
                      </TimeVolumeContainer>
                    </ColumnStart>
                  </InfoWrapper>
                }
              />
            </ChartWrapper>
          </Carousel>
        ) : (
          <ResponsiveRow>
            <ChartWrapper>
              <LineChart
                data={
                  tvlWindow === VolumeWindow.monthly
                    ? monthlyTVLData
                    : tvlWindow === VolumeWindow.weekly
                    ? weeklyTVLData
                    : formattedTvlData
                }
                height={220}
                minHeight={332}
                value={liquidityHover}
                label={leftLabel}
                setValue={setLiquidityHover}
                setLabel={setLeftLabel}
                activeWindow={tvlWindow}
                topRight={
                  <WindowSwitchContainer>
                    <SmallOptionButton onClick={() => setTvlWindow(VolumeWindow.daily)}>
                      <SwitchOption active={tvlWindow === VolumeWindow.daily}>D</SwitchOption>
                    </SmallOptionButton>
                    {/*<SmallOptionButton style={{ marginLeft: '8px' }} onClick={() => setTvlWindow(VolumeWindow.weekly)}>
                      <SwitchOption active={tvlWindow === VolumeWindow.weekly}>W</SwitchOption>
                </SmallOptionButton>
                    <SmallOptionButton style={{ marginLeft: '8px' }} onClick={() => setTvlWindow(VolumeWindow.monthly)}>
                      <SwitchOption active={tvlWindow === VolumeWindow.monthly}>M</SwitchOption>
                    </SmallOptionButton>*/}
                  </WindowSwitchContainer>
                }
                topLeft={
                  <InfoWrapper>
                    <InfoContainer>
                      <LeftAlignContainer>
                        <ChartTitle>TVL</ChartTitle>
                        <ChartSubTitle>Total Value Locked</ChartSubTitle>
                      </LeftAlignContainer>
                    </InfoContainer>
                    <InfoSeparator />
                    <InfoContainer>
                      <LeftAlignContainer>
                        <ChartTitle>{tvlValue}</ChartTitle>
                        <TimeVolumeContainer>
                          <img width={'20px'} height={'20px'} src={CalendarIcon} alt="Calendar" />
                          <ChartSubTitle>{leftLabel ? <MonoSpace>{leftLabel}</MonoSpace> : 'All time'}</ChartSubTitle>
                        </TimeVolumeContainer>
                      </LeftAlignContainer>
                    </InfoContainer>
                  </InfoWrapper>
                }
              />
            </ChartWrapper>
            <ChartWrapper>
              <BarChart
                height={220}
                minHeight={332}
                data={
                  volumeWindow === VolumeWindow.monthly
                    ? monthlyVolumeData
                    : volumeWindow === VolumeWindow.weekly
                    ? weeklyVolumeData
                    : formattedVolumeData
                }
                setValue={setVolumeHover}
                setLabel={setRightLabel}
                value={volumeHover}
                label={rightLabel}
                activeWindow={volumeWindow}
                topRight={
                  <WindowSwitchContainer>
                    <SmallOptionButton onClick={() => setVolumeWindow(VolumeWindow.daily)}>
                      <SwitchOption active={volumeWindow === VolumeWindow.daily}>D</SwitchOption>
                    </SmallOptionButton>
                    <SmallOptionButton
                      style={{ marginLeft: '8px' }}
                      onClick={() => setVolumeWindow(VolumeWindow.weekly)}
                    >
                      <SwitchOption active={volumeWindow === VolumeWindow.weekly}>W</SwitchOption>
                    </SmallOptionButton>
                    <SmallOptionButton
                      style={{ marginLeft: '8px' }}
                      onClick={() => setVolumeWindow(VolumeWindow.monthly)}
                    >
                      <SwitchOption active={volumeWindow === VolumeWindow.monthly}>M</SwitchOption>
                    </SmallOptionButton>
                  </WindowSwitchContainer>
                }
                topLeft={
                  <InfoWrapper>
                    <InfoContainer>
                      <LeftAlignContainer>
                        <ChartTitle>Volume</ChartTitle>
                        <TYPE.subHeader fontSize="12px">24h Volume</TYPE.subHeader>
                      </LeftAlignContainer>
                    </InfoContainer>
                    <InfoSeparator />
                    <InfoContainer>
                      <LeftAlignContainer>
                        <ChartTitle>{formatDollarAmount(volumeHover, 2)}</ChartTitle>
                        <TimeVolumeContainer>
                          <img width={'20px'} height={'20px'} src={CalendarIcon} alt="Calendar" />
                          <ChartSubTitle>{rightLabel ? <MonoSpace>{rightLabel}</MonoSpace> : 'All time'}</ChartSubTitle>
                        </TimeVolumeContainer>
                      </LeftAlignContainer>
                    </InfoContainer>
                  </InfoWrapper>
                }
              />
            </ChartWrapper>
          </ResponsiveRow>
        )}

        {isMobile ? (
          <AnimatedElements>
            <ScrollContainer>
              <DailyInfoElement
                title="24hs Volume"
                value={formatDollarAmount(protocolData?.volumeUSD)}
                percentageChange={protocolData?.volumeUSDChange}
                icon={EqualizerIcon}
                isMobile={isMobile}
              />
              <DailyInfoElement
                title="24hs Fees"
                value={formatDollarAmount(protocolData?.feesUSD)}
                percentageChange={protocolData?.feeChange}
                icon={InsightsIcon}
                isMobile={isMobile}
              />
              <DailyInfoElement
                title="Total Value Lockedd"
                value={formatDollarAmount(protocolData?.tvlUSD)}
                percentageChange={protocolData?.tvlUSDChange}
                icon={LockIcon}
                isMobile={isMobile}
              />
            </ScrollContainer>

            <ReverseScrollContainer>
              <DailyInfoElement
                title="Current Epoch"
                value={`Epoch ${getCurrentEpoch()}`}
                icon={EmptyHourGlassIcon}
                isMobile={isMobile}
              />
              <DailyInfoElement
                title="Epoch Starts"
                value={getEpochStart()}
                icon={EmptyHourGlassIcon}
                timeInfo={'2:01 UTC'}
                isMobile={isMobile}
              />
              <DailyInfoElement
                title="Epoch End"
                value={getEpochEnd()}
                icon={EmptyHourGlassIcon}
                timeInfo={'2:00 UTC'}
                isMobile={isMobile}
              />
            </ReverseScrollContainer>
          </AnimatedElements>
        ) : (
          <InfoRowContainer>
            <DailyInfoElement
              title="24hs Volume"
              value={formatDollarAmount(protocolData?.volumeUSD)}
              percentageChange={protocolData?.volumeUSDChange}
              icon={EqualizerIcon}
              isMobile={isMobile}
            />
            <DailyInfoElement
              title="24hs Fees"
              value={formatDollarAmount(protocolData?.feesUSD)}
              percentageChange={protocolData?.feeChange}
              icon={InsightsIcon}
              isMobile={isMobile}
            />
            <DailyInfoElement
              title="Total Value Locked"
              value={formatDollarAmount(protocolData?.tvlUSD)}
              percentageChange={protocolData?.tvlUSDChange}
              icon={LockIcon}
              isMobile={isMobile}
            />

            <DailyInfoElement
              title="Current Epoch"
              value={`Epoch ${getCurrentEpoch()}`}
              icon={EmptyHourGlassIcon}
              isMobile={isMobile}
            />
            <DailyInfoElement
              title="Epoch Starts"
              value={getEpochStart()}
              icon={EmptyHourGlassIcon}
              timeInfo={'2:01 UTC'}
              isMobile={isMobile}
            />
            <DailyInfoElement
              title="Epoch End"
              value={getEpochEnd()}
              icon={EmptyHourGlassIcon}
              timeInfo={'2:00 UTC'}
              isMobile={isMobile}
            />
          </InfoRowContainer>
        )}

        {/*<TableRowBetween>
          <PageSubHeader>veCHR Analytics</PageSubHeader>
          <ExploreLink to="vechr-analytics">
            <LinkContent>Explore</LinkContent>
            <ExploreIconContainer>
              <img height={20} width={20} alt="arrow" src={ArrowRightIcon} />
            </ExploreIconContainer>
          </ExploreLink>
        </TableRowBetween>

        <InfoRowContainer>
          <DailyInfoElement
            title="Total veCHR"
            // value="asdfasdf"
            value={`${veChrAmount ? formatAmount(veChrAmount) : 0} veCHR`}
            icon={ChronosHourglass}
          />
          <DailyInfoElement title="Average Locked Duration" value="1.86 Years" icon={LockResetIcon} />
          <DailyInfoElement title="veCHR Holders" value="3,919 Holders" icon={AccountIcon} />
        </InfoRowContainer> 
        </PageWrapper>*/}

        <TableRowBetween>
          {isMobile ? (
            <ColumnStart>
              <TYPE.subHeader fontSize="18px">Tokens</TYPE.subHeader>
              <RowFixed>
                <img src={TopPositionIcon} height={16} width={16} alt="cup" style={{ opacity: '0.5' }} />
                <TYPE.subHeader color={theme.textWithOpacity}>Top positions</TYPE.subHeader>
              </RowFixed>
            </ColumnStart>
          ) : (
            <TYPE.subHeader fontSize="30px">Tokens</TYPE.subHeader>
          )}

          <ExploreLink to="tokens">
            <LinkContent>Explore</LinkContent>
            <ExploreIconContainer>
              <img height={20} width={20} alt="arrow" src={ArrowRightIcon} />
            </ExploreIconContainer>
          </ExploreLink>
        </TableRowBetween>
        <TokenTable tokenDatas={formattedTokens} />
        <TableRowBetween>
          {isMobile ? (
            <ColumnStart>
              <TYPE.subHeader fontSize="18px">Pools</TYPE.subHeader>
              <RowFixed>
                <img src={TopPositionIcon} height={16} width={16} alt="cup" style={{ opacity: '0.5' }} />
                <TYPE.subHeader color={theme.textWithOpacity}>Top positions</TYPE.subHeader>
              </RowFixed>
            </ColumnStart>
          ) : (
            <TYPE.subHeader fontSize="30px">Pools</TYPE.subHeader>
          )}
          <ExploreLink to="pools">
            <LinkContent>Explore</LinkContent>
            <ExploreIconContainer>
              <img height={20} width={20} alt="arrow" src={ArrowRightIcon} />
            </ExploreIconContainer>
          </ExploreLink>
        </TableRowBetween>
        <PoolTable poolDatas={Object.values(allPoolData)} />

        <TableRowBetween>
          {isMobile ? (
            <ColumnStart>
              <TYPE.subHeader fontSize="18px">Gauges</TYPE.subHeader>
              <RowFixed>
                <img src={TopPositionIcon} height={16} width={16} alt="cup" style={{ opacity: '0.5' }} />
                <TYPE.subHeader color={theme.textWithOpacity}>Top positions</TYPE.subHeader>
              </RowFixed>
            </ColumnStart>
          ) : (
            <TYPE.subHeader fontSize="30px">Gauges</TYPE.subHeader>
          )}
          <ExploreLink to="pools">
            <LinkContent>Explore</LinkContent>
            <ExploreIconContainer>
              <img height={20} width={20} alt="arrow" src={ArrowRightIcon} />
            </ExploreIconContainer>
          </ExploreLink>
        </TableRowBetween>
        {/* <GaugeTable  /> */}
        <TableRowBetween>
          <PageSubHeader>Transactions</PageSubHeader>
          <ExploreLink to="transactions">
            <LinkContent>Explore</LinkContent>
            <ExploreIconContainer>
              <img height={20} width={20} alt="arrow" src={ArrowRightIcon} />
            </ExploreIconContainer>
          </ExploreLink>
        </TableRowBetween>
        {transactions ? <TransactionsTable transactions={transactions} color={activeNetwork.primaryColor} /> : null}
      </AutoColumn>
    </PageWrapper>
  )
}
